import React, { useEffect, useState, useCallback } from "react";
import { api, fmtMoney, fmtPct } from "../lib/api";
import { Star, Sparkles } from "lucide-react";
import { toast } from "sonner";

// ── Discover surface — ranked top-N cards per metric ──
// One card per ranking. Each row inside is one BDC with the metric value
// + a star-toggle to add to watchlist directly without going to /database/bdc.

const FMT = {
  pct:        (v) => fmtPct(v),                                  // dividend_yield (already %)
  pct_raw:    (v) => `${Number(v).toFixed(1)}%`,                 // payout (already %)
  pct_signed: (v) => `${v > 0 ? "+" : ""}${Number(v).toFixed(1)}%`,
  multiple:   (v) => `${Number(v).toFixed(2)}×`,
  raw_2dp:    (v) => Number(v).toFixed(2),
  usd_b:      (v) => `$${(v / 1_000_000_000).toFixed(2)}B`,
};

export default function DatabaseBDCDiscover() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [watchedSet, setWatchedSet] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: discover }, { data: watch }] = await Promise.all([
        api.get("/database/discover/bdc"),
        api.get("/watchlist").catch(() => ({ data: [] })),
      ]);
      setData(discover);
      setWatchedSet(new Set((watch || []).map((w) => w.ticker)));
    } catch (e) {
      toast.error("Failed to load Discover");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleStar = async (ticker) => {
    const was = watchedSet.has(ticker);
    setWatchedSet((s) => {
      const next = new Set(s);
      if (was) next.delete(ticker); else next.add(ticker);
      return next;
    });
    try {
      if (was) await api.delete(`/watchlist/${ticker}`);
      else await api.post(`/watchlist/${ticker}`);
      toast.success(was ? `${ticker} unwatched` : `${ticker} added to watchlist`);
    } catch {
      setWatchedSet((s) => {
        const next = new Set(s);
        if (was) next.add(ticker); else next.delete(ticker);
        return next;
      });
      toast.error("Watchlist update failed");
    }
  };

  return (
    <div className="min-h-screen bg-champagne">
      <div className="border-b border-stone px-8 py-5 sticky top-0 bg-champagne z-10">
        <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold">// SYS::DATABASE::BDC::DISCOVER</div>
        <h1 className="text-2xl font-bold tracking-tight text-graphite flex items-center gap-2" data-testid="bdc-discover-title">
          <Sparkles className="w-5 h-5 text-paars-dark" /> Discover BDCs
        </h1>
        <p className="text-xs text-graphite/55 mt-1 max-w-2xl">
          Six opinionated top-10 lists computed from the same data on the BDC Database.
          Click a row to view the ticker · star it to receive cut-risk emails.
        </p>
      </div>

      <div className="p-8">
        {loading ? (
          <div className="text-graphite/55 font-mono text-sm">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {(data?.rankings || []).map((rk) => (
              <RankingCard
                key={rk.key}
                ranking={rk}
                watchedSet={watchedSet}
                onToggleStar={toggleStar}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RankingCard({ ranking, watchedSet, onToggleStar }) {
  const fmt = FMT[ranking.metric_format] || ((v) => String(v));
  return (
    <div className="border border-stone bg-champagne" data-testid={`discover-card-${ranking.key}`}>
      <div className="px-5 py-3 border-b border-stone">
        <div className="text-[10px] tracking-[0.25em] uppercase text-paars-dark font-bold mb-1">// {ranking.key.replace(/_/g, " ").toUpperCase()}</div>
        <h3 className="text-sm font-bold text-graphite">{ranking.title}</h3>
        <p className="text-[11px] text-graphite/55 mt-0.5">{ranking.subtitle}</p>
      </div>
      <div className="divide-y divide-stone/60">
        {ranking.rows.length === 0 && (
          <div className="px-5 py-6 text-center text-[11px] font-mono text-graphite/45">
            No data yet — fundamentals being collected.
          </div>
        )}
        {ranking.rows.map((r, idx) => {
          const v = r[ranking.metric_key];
          return (
            <div
              key={r.ticker}
              data-testid={`discover-row-${ranking.key}-${r.ticker}`}
              className="flex items-center gap-3 px-5 py-2.5 hover:bg-white transition-colors"
            >
              <span className="font-mono text-[10px] text-graphite/45 w-5 text-right">{idx + 1}</span>
              <button
                data-testid={`discover-star-${ranking.key}-${r.ticker}`}
                onClick={() => onToggleStar(r.ticker)}
                aria-pressed={watchedSet.has(r.ticker)}
                title={watchedSet.has(r.ticker) ? "Unwatch" : "Watch"}
                className={`p-0.5 transition-colors ${watchedSet.has(r.ticker) ? "text-gold-dark" : "text-graphite/30 hover:text-gold-dark"}`}
              >
                <Star className="w-3.5 h-3.5" fill={watchedSet.has(r.ticker) ? "currentColor" : "none"} />
              </button>
              <div className="font-mono font-semibold text-paars-dark text-sm w-12">{r.ticker}</div>
              <div className="text-xs text-graphite/65 truncate flex-1">{r.name || ""}</div>
              <div className="font-mono text-sm text-graphite tabular-nums">{v != null ? fmt(v) : "—"}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
